import {store} from "../../store/store";
import Alert from "../../components/ui/alert/Alert";
import React, {useEffect, useState} from "react";
import PageMeta from "../../components/common/PageMeta";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import ComponentCard from "../../components/common/ComponentCard";
import Label from "../../components/form/Label";
import Input from "../../components/form/input/InputField";
import { apiRequestWithAuth } from "../../utils/api";
import FileInput from "../../components/form/input/FileInput";
import {useNavigate} from "react-router-dom";
import DatePicker from "react-datepicker";
import CustomDateInput from "../../components/form/input/date/CustomDateInput";
import {EyeIcon} from "lucide-react";
import {EyeCloseIcon} from "../../icons";
import {Textarea} from "@mantine/core";
import {setUser} from "../../slices/authSlice";
import {useDispatch} from "react-redux";

const EditProfile = () => {
    const user = store.getState().auth.user;
    const defaultForm = {
        name: "",
        phoneNumber: "",
        password: "",
        avatar: "",
        yearOfBirth: "",
        gender: "",
        goal: "",
        occupation: "",
        skill: "",
        bio: "",
    };

    const [showAlert, setShowAlert] = useState(false);
    const [showErrAlert, setShowErrAlert] = useState("");
    const [formData, setFormData] = useState(defaultForm);
    const [avatar, setAvatar] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const navigate = useNavigate();

    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [password, setPassword] = useState(""); // State lưu password
    const [rePassword, setRePassword] = useState(""); // State lưu re-password
    const [showRePassword, setShowRePassword] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const dispatch = useDispatch();

    // Fetch existing course
    useEffect(() => {
        const fetchPost = async () => {
            try {
                const res = await apiRequestWithAuth(`/profile`, "GET", {});
                const data = res.result;

                setFormData({
                    name: data.name,
                    phoneNumber: data.phoneNumber,
                    password: "",
                    avatar: "",
                    yearOfBirth: data.yearOfBirth,
                    gender: data.gender,
                    goal: data.goal,
                    occupation: data.occupation,
                    skill: data.skill,
                    bio: data.bio,
                });

                // Lấy ảnh đầu tiên nếu có
                if (data && data?.avatarImage.publicPath) {
                    setAvatar(data?.avatarImage?.publicPath);
                }
            } catch (error) {
                console.error("Lỗi khi lấy dữ liệu slider:", error);
            }
        };

        if (user?.id) fetchPost();
    }, [user?.id]);

    const parseDate = (dateStr: any): Date | null => {
        if (!dateStr) return null;
        const [day, month, year] = dateStr.split("-");
        return new Date(Number(year), Number(month) - 1, Number(day));
    };

    const formatDate = (date: Date): string => {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0'); // tháng bắt đầu từ 0
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleUploadImage = async (file: File) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("subject", "avatar");

        try {
            const res = await apiRequestWithAuth('/media/file', 'POST', formData);
            return res.result?.id || null;
        } catch (error) {
            console.error("Upload image failed", error);
            return null;
        }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            setAvatar(URL.createObjectURL(file));
            const uploadedId = await handleUploadImage(file);

            const name = "avatar";
            const value = uploadedId;
            setFormData((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
    };

    const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handlePickerChange = async (date: Date | null) => {
        const name = "yearOfBirth";
        const value = date ? formatDate(date) : "";
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSave = async () => {
        if (password !== rePassword) {
            setError("Passwords do not match!");
            return;
        }
        try {
            const response = await apiRequestWithAuth(`/profile`, "PUT", formData);
            if (response.messageCode == 200) {
                dispatch(setUser(response.result));
                setShowAlert(true)
                setTimeout(() => navigate("/profile"), 1000);
            } else {
                setError(response.message || "Cập nhật thất bại.");
                setShowErrAlert(response.message || "Cập nhật thất bại.");
                return;
            }
            setFormData(defaultForm);
            setSelectedFile(null);
        } catch (error) {
            console.error("Update post failed", error);
        }
    };

  return (
      <div>
          {showAlert && (
              <Alert variant="success" title="Success" message="User profile successfully." />
          )}
          {showErrAlert && (
              <Alert variant="error" title="Error" message={showErrAlert} />
          )}
          <PageMeta title="Update Profile" description="Update Profile details" />
          <PageBreadcrumb pageTitle="Update Profile" />

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-1">
              <div className="space-y-6">
                  <ComponentCard title="User Information">
                      <div className="space-y-6">
                          <div>
                              <Label>Name</Label>
                              <Input type="text" name="name" value={formData.name} onChange={handleChange}/>
                          </div>
                      </div>
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                          {avatar && (
                              <div className="mt-2">
                                  <img src={avatar} alt="Avatar Preview"
                                       className="w-32 h-32 object-cover rounded-full border"/>
                              </div>
                          )}
                          <div className="mt-2">
                              <Label>Avatar</Label>
                              <FileInput onChange={handleFileChange}/>
                          </div>
                      </div>
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                          <div>
                              <Label>Phone Number</Label>
                              <Input type="text" name="phoneNumber" value={formData.phoneNumber ?? ""} onChange={handleChange}/>
                          </div>
                          <div>
                              <Label>Email</Label>
                              <Input type="email" name="email" value={user?.email} disabled/>
                          </div>
                      </div>
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                          <div>
                              <Label htmlFor="datePicker">Ngày sinh</Label>
                              <div className="relative">
                                  <DatePicker
                                      selected={parseDate(formData.yearOfBirth)}
                                      onChange={handlePickerChange}
                                      dateFormat="dd/MM/yyyy"
                                      customInput={<CustomDateInput/>}
                                      placeholderText="dd/mm/yyyy"
                                      isClearable
                                      showYearDropdown
                                      showMonthDropdown
                                      dropdownMode="select"
                                      wrapperClassName="w-full"
                                      autoComplete="off"
                                  />
                              </div>
                          </div>
                          <div>
                              <Label>Giới tính</Label>
                              <select
                                  name="gender"
                                  value={formData.gender}
                                  onChange={handleChange}
                                  className="block w-full border border-gray-300 rounded-md shadow-sm p-2"
                              >
                                  <option value="">-- Chọn giới tính --</option>
                                  <option value="1">Nam</option>
                                  <option value="2">Nữ</option>
                              </select>
                          </div>
                      </div>
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                          <div>
                              <Label>Password</Label>
                              <div className="relative">
                                  <Input
                                      type={showPassword ? "text" : "password"}
                                      placeholder="Enter your password"
                                      value={password}
                                      name="password"
                                      onChange={(e) => setPassword(e.target.value)}
                                  />
                                  <button
                                      type="button"
                                      onClick={() => setShowPassword(!showPassword)}
                                      className="absolute z-30 -translate-y-1/2 cursor-pointer right-4 top-1/2"
                                  >
                                      {showPassword ? <EyeIcon className="fill-gray-500"/> :
                                          <EyeCloseIcon className="fill-gray-500"/>}
                                  </button>
                              </div>
                          </div>
                          <div>
                              <Label>Re-Enter Password</Label>
                              <div className="relative">
                                  <Input
                                      type={showRePassword ? "text" : "password"}
                                      name="rePassword"
                                      placeholder="Confirm your password"
                                      value={rePassword}
                                      onChange={(e) => setRePassword(e.target.value)}
                                  />
                                  <button
                                      type="button"
                                      onClick={() => setShowRePassword(!showRePassword)}
                                      className="absolute z-30 -translate-y-1/2 cursor-pointer right-4 top-1/2"
                                  >
                                      {showRePassword ? <EyeIcon className="fill-gray-500"/> :
                                          <EyeCloseIcon className="fill-gray-500"/>}
                                  </button>
                              </div>
                              {error && <p className="text-red-500">{error}</p>}
                          </div>
                      </div>
                      <div>
                          <Label>Goal (Mục tiêu)</Label>
                          <Textarea name="goal" value={formData.goal} onChange={handleTextareaChange}/>
                      </div>
                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                          <div>
                              <Label>Occupation (Nghề nghiệp)</Label>
                              <Input type="text" name="occupation" value={formData.occupation} onChange={handleChange}/>
                          </div>
                          <div>
                              <Label>Skill (Chuyên môn/Kỹ năng)</Label>
                              <Input type="text" name="skill" value={formData.skill} onChange={handleChange}/>
                          </div>
                      </div>
                      <div>
                          <Label>Bio (Quá trình học tập)</Label>
                          <Textarea name="bio" value={formData.bio} onChange={handleTextareaChange}/>
                      </div>
                  </ComponentCard>
              </div>
              <button onClick={handleSave}
                      className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300">
                  Lưu
              </button>
          </div>

      </div>
  );
};
export default EditProfile